import { useEffect, type ReactNode } from 'react';
import {
  ArrowDownTrayIcon,
  CheckCircleIcon,
  CubeIcon,
  EyeIcon,
  EyeSlashIcon,
  InformationCircleIcon,
  XCircleIcon,
  XMarkIcon,
} from '@heroicons/react/24/outline';
import { useForm } from 'react-hook-form';
import { Link } from 'react-router';

import { Alert, Button } from '@shared';
import type { ProjectOrderScanFile } from '@/services/api/types';
import { parseUtcDate } from '../utils/date-time';

export type DecisionOutcome = 'accepted' | 'rejected';
export type PendingDecision = DecisionOutcome | null;

export interface OrderReviewFileLabels {
  eyebrow: string;
  title: string;
  description: string;
  count: string;
  layerControls: string;
  showAll: string;
  hideAll: string;
  opacity: string;
  hideAction: string;
  viewAction: string;
  downloadAction: string;
  downloadBusy: string;
  viewerLoading: string;
  noFiles: string;
  reviewHint: string;
}

interface OrderReviewFilesPanelProps {
  files: readonly ProjectOrderScanFile[];
  labels: OrderReviewFileLabels;
  visibleFileIds: readonly string[];
  loadingFileIds: ReadonlySet<string>;
  viewerOpacities: Readonly<Record<string, number>>;
  downloadingFileId: string | null;
  allFilesVisible: boolean;
  downloadError: ReactNode;
  onShowAll: () => void;
  onHideAll: () => void;
  onToggleVisibility: (file: ProjectOrderScanFile) => void;
  onDownload: (file: ProjectOrderScanFile) => void;
  onOpacityChange: (fileId: string, value: string) => void;
}

function formatFileSize(sizeInKB: number | null | undefined): string {
  if (typeof sizeInKB !== 'number') {
    return '-';
  }

  const numberFormat = new Intl.NumberFormat('de-DE', { maximumFractionDigits: 1 });
  if (sizeInKB < 1024) {
    return `${numberFormat.format(sizeInKB)} KB`;
  }

  return `${numberFormat.format(sizeInKB / 1024)} MB`;
}

function formatDateTime(value: string): string {
  return new Intl.DateTimeFormat('de-DE', {
    day: '2-digit',
    month: '2-digit',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  }).format(parseUtcDate(value));
}

export function OrderReviewFilesPanel({
  files,
  labels,
  visibleFileIds,
  loadingFileIds,
  viewerOpacities,
  downloadingFileId,
  allFilesVisible,
  downloadError,
  onShowAll,
  onHideAll,
  onToggleVisibility,
  onDownload,
  onOpacityChange,
}: OrderReviewFilesPanelProps) {
  return (
    <section className="md-review__panel order-1 flex min-h-0 min-w-0 flex-col lg:order-none lg:col-start-1 lg:row-start-1">
      <div className="flex items-start justify-between gap-4">
        <div>
          <span className="md-review__section-eyebrow">{labels.eyebrow}</span>
          <h2 className="mb-[0.35rem] text-[1.2rem]" id="order-review-files-title">
            {labels.title}
          </h2>
        </div>
        <span className="shrink-0 rounded-full bg-[var(--md-primary-light)] px-[0.55rem] py-1 text-xs font-bold text-[var(--md-primary)]">
          {labels.count}
        </span>
      </div>
      <p className="m-0 mt-[0.35rem] mb-4 text-[var(--zs-muted)]">{labels.description}</p>
      <div className="mb-4 flex flex-wrap items-center justify-between gap-2 rounded-[var(--zs-radius-s)] border border-[var(--md-border)] bg-[var(--md-bg)] px-3 py-2.5">
        <span className="text-xs font-bold tracking-[0.04em] text-[var(--zs-muted)] uppercase">
          {labels.layerControls}
        </span>
        <div className="flex flex-wrap gap-2">
          <Button
            type="button"
            variant="secondary"
            className="h-9 px-3 text-xs"
            disabled={files.length === 0 || allFilesVisible}
            onClick={onShowAll}
            aria-pressed={allFilesVisible}
          >
            <EyeIcon className="h-4 w-4 shrink-0" aria-hidden="true" />
            <span>{labels.showAll}</span>
          </Button>
          <Button
            type="button"
            variant="secondary"
            className="h-9 px-3 text-xs"
            disabled={visibleFileIds.length === 0}
            onClick={onHideAll}
          >
            <EyeSlashIcon className="h-4 w-4 shrink-0" aria-hidden="true" />
            <span>{labels.hideAll}</span>
          </Button>
        </div>
      </div>
      <div className="min-h-0 flex-1 overflow-y-auto overscroll-contain">
        {files.length === 0 ? (
          <p className="m-0 rounded-[var(--zs-radius-s)] border border-[var(--md-border)] bg-[var(--md-bg)] p-4 text-[var(--zs-muted)]">
            {labels.noFiles}
          </p>
        ) : (
          <ul className="m-0 flex list-none flex-col p-0" aria-labelledby="order-review-files-title">
            {files.map((file) => {
              const fileId = file.id?.trim() ?? '';
              const isDownloading = downloadingFileId === fileId;
              const isVisible = visibleFileIds.includes(fileId);
              const isLayerLoading = loadingFileIds.has(fileId);
              const viewerOpacity = fileId ? (viewerOpacities[fileId] ?? 100) : 100;

              return (
                <li
                  key={file.id ?? file.name}
                  className="flex min-h-[3.35rem] min-w-0 items-center justify-between gap-[0.85rem] border-b border-[var(--md-border)] px-[0.35rem] py-[0.55rem] hover:bg-[var(--md-bg)] max-sm:items-start max-sm:flex-col"
                >
                  <div className="flex w-full min-w-0 flex-1 items-center gap-2.5">
                    <CubeIcon
                      className="h-[1.3rem] w-[1.3rem] shrink-0 text-[var(--md-primary)]"
                      aria-hidden="true"
                    />
                    <div className="flex min-w-0 w-full flex-1 flex-col gap-1">
                      <strong className="[overflow-wrap:anywhere] text-sm leading-[1.35] text-[var(--md-primary)]">
                        {file.name}
                      </strong>
                      <span className="text-[0.6875rem] leading-[1.35] text-[var(--zs-muted)]">
                        {file.type} / {formatFileSize(file.sizeInKB)} / {formatDateTime(file.uploadedAtUtc)}
                      </span>
                      <label className="flex w-full items-center gap-2 text-xs font-semibold text-[var(--md-primary)]">
                        <span className="shrink-0">{labels.opacity}</span>
                        <input
                          className="w-full min-w-0 flex-1 accent-[var(--md-highlight)]"
                          type="range"
                          min="0"
                          max="100"
                          step="1"
                          value={viewerOpacity}
                          onChange={(event) => onOpacityChange(fileId, event.target.value)}
                          aria-label={`${labels.opacity} ${file.name}`}
                        />
                        <output className="w-[3.25rem] text-right font-mono text-[0.6875rem]">
                          {viewerOpacity}%
                        </output>
                      </label>
                    </div>
                  </div>
                  <div className="flex shrink-0 gap-2 max-sm:self-end">
                    <Button
                      type="button"
                      variant={isVisible ? 'primary' : 'secondary'}
                      className="h-9 w-9 min-w-[2.25rem] shrink-0 p-0"
                      disabled={!fileId || isLayerLoading}
                      aria-busy={isLayerLoading || undefined}
                      onClick={() => onToggleVisibility(file)}
                      aria-label={`${isVisible ? labels.hideAction : labels.viewAction} ${file.name}`}
                      aria-pressed={isVisible}
                      title={`${isVisible ? labels.hideAction : labels.viewAction} ${file.name}`}
                    >
                      {isLayerLoading ? (
                        <span
                          className="md-review__layer-spinner"
                          role="status"
                          aria-label={labels.viewerLoading}
                        >
                          <span className="zs-spinner__dot" aria-hidden="true" />
                        </span>
                      ) : isVisible ? (
                        <EyeSlashIcon className="h-[1.15rem] w-[1.15rem] shrink-0" aria-hidden="true" />
                      ) : (
                        <EyeIcon className="h-[1.15rem] w-[1.15rem] shrink-0" aria-hidden="true" />
                      )}
                    </Button>
                    <Button
                      type="button"
                      variant="secondary"
                      className="h-9 w-9 min-w-[2.25rem] shrink-0 p-0"
                      disabled={!fileId || isDownloading || Boolean(downloadingFileId)}
                      aria-busy={isDownloading || undefined}
                      onClick={() => onDownload(file)}
                      aria-label={`${labels.downloadAction} ${file.name}`}
                      title={`${labels.downloadAction} ${file.name}`}
                    >
                      {isDownloading ? (
                        <span
                          className="md-review__layer-spinner"
                          role="status"
                          aria-label={labels.downloadBusy}
                        >
                          <span className="zs-spinner__dot" aria-hidden="true" />
                        </span>
                      ) : (
                        <ArrowDownTrayIcon className="h-[1.15rem] w-[1.15rem] shrink-0" aria-hidden="true" />
                      )}
                    </Button>
                  </div>
                </li>
              );
            })}
          </ul>
        )}
      </div>
      {downloadError ? (
        <p className="m-0 mt-3.5 text-sm text-[var(--md-highlight)]">{downloadError}</p>
      ) : null}
      <p className="m-0 mt-3.5 flex items-start gap-2 border-t border-[var(--md-border)] pt-3.5 text-[0.8125rem] leading-[1.4] text-[var(--zs-muted)]">
        <InformationCircleIcon className="h-4 w-4 shrink-0 text-[var(--md-primary)]" aria-hidden="true" />
        <span>{labels.reviewHint}</span>
      </p>
    </section>
  );
}

export interface OrderReviewDecisionLabels {
  eyebrow: string;
  title: string;
  description: string;
  accept: string;
  accepting: string;
  reject: string;
  rejecting: string;
  accepted: string;
  rejected: string;
  backToOrder: string;
}

export function OrderReviewDecisionPanel({
  orderId,
  labels,
  decisionOutcome,
  pendingDecision,
  canReview,
  errorMessage,
  onAccept,
  onReject,
}: {
  orderId: string;
  labels: OrderReviewDecisionLabels;
  decisionOutcome: DecisionOutcome | null;
  pendingDecision: PendingDecision;
  canReview: boolean;
  errorMessage: string | null;
  onAccept: () => void;
  onReject: () => void;
}) {
  return (
    <section className="md-review__panel order-3 grid items-center gap-x-6 gap-y-4 border-l-4 border-l-[var(--md-highlight)] lg:order-none lg:col-span-2 lg:grid-cols-[minmax(0,1fr)_auto]">
      <div className="min-w-0">
        <span className="md-review__section-eyebrow">{labels.eyebrow}</span>
        <h2 className="mb-[0.35rem] text-[1.2rem]">{labels.title}</h2>
        <p className="m-0 mt-[0.35rem] mb-4 text-[var(--zs-muted)]">{labels.description}</p>
        {errorMessage ? <Alert variant="error">{errorMessage}</Alert> : null}
        {decisionOutcome ? (
          <Alert variant="success">
            {decisionOutcome === 'accepted' ? labels.accepted : labels.rejected}
          </Alert>
        ) : null}
      </div>
      <div className="grid grid-cols-2 gap-[0.7rem] max-sm:grid-cols-1 lg:col-start-2 lg:row-start-1 lg:self-center">
        <Button
          type="button"
          loading={pendingDecision === 'accepted'}
          loadingLabel={labels.accepting}
          disabled={!canReview}
          onClick={onAccept}
        >
          <CheckCircleIcon className="h-[1.15rem] w-[1.15rem] shrink-0" aria-hidden="true" />
          <span>{labels.accept}</span>
        </Button>
        <Button
          type="button"
          variant="danger"
          loading={pendingDecision === 'rejected'}
          loadingLabel={labels.rejecting}
          disabled={!canReview}
          onClick={onReject}
        >
          <XCircleIcon className="h-[1.15rem] w-[1.15rem] shrink-0" aria-hidden="true" />
          <span>{labels.reject}</span>
        </Button>
      </div>
      {decisionOutcome ? (
        <Link className="mt-4 font-bold lg:col-span-2" to={`/cases/${orderId}`}>
          {labels.backToOrder}
        </Link>
      ) : null}
    </section>
  );
}

interface RejectionFormValues {
  reason: string;
}

export interface OrderReviewRejectionLabels {
  eyebrow: string;
  title: string;
  description: string;
  reasonLabel: string;
  reasonPlaceholder: string;
  reasonRequired: string;
  close: string;
  cancel: string;
  submit: string;
  submitting: string;
}

export function OrderReviewRejectionDialog({
  labels,
  isPending,
  onClose,
  onSubmit,
}: {
  labels: OrderReviewRejectionLabels;
  isPending: boolean;
  onClose: () => void;
  onSubmit: (reason: string) => void;
}) {
  const form = useForm<RejectionFormValues>({ defaultValues: { reason: '' } });
  const reasonError = form.formState.errors.reason?.message;

  useEffect(() => {
    const handleKeyDown = (event: KeyboardEvent): void => {
      if (event.key === 'Escape' && !isPending) {
        onClose();
      }
    };

    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [isPending, onClose]);

  return (
    <div className="md-review-dialog" role="presentation">
      <section
        className="md-review-dialog__panel"
        role="dialog"
        aria-modal="true"
        aria-labelledby="order-review-rejection-title"
        aria-describedby="order-review-rejection-description"
      >
        <div className="md-review-dialog__header">
          <div>
            <span className="md-review__section-eyebrow">{labels.eyebrow}</span>
            <h2 id="order-review-rejection-title">{labels.title}</h2>
          </div>
          <button
            type="button"
            className="zs-button zs-button--secondary md-icon-button"
            aria-label={labels.close}
            title={labels.close}
            disabled={isPending}
            onClick={onClose}
          >
            <XMarkIcon className="md-icon-button__icon" aria-hidden="true" />
          </button>
        </div>
        <p id="order-review-rejection-description" className="md-review-dialog__description">
          {labels.description}
        </p>
        <form
          className="md-review-dialog__form"
          noValidate
          onSubmit={form.handleSubmit(({ reason }) => onSubmit(reason.trim()))}
        >
          <label htmlFor="order-review-rejection-reason">{labels.reasonLabel}</label>
          <textarea
            id="order-review-rejection-reason"
            className="md-review-dialog__textarea"
            rows={6}
            maxLength={4000}
            autoFocus
            aria-invalid={Boolean(reasonError) || undefined}
            aria-describedby={reasonError ? 'order-review-rejection-reason-error' : undefined}
            placeholder={labels.reasonPlaceholder}
            disabled={isPending}
            {...form.register('reason', {
              required: labels.reasonRequired,
              validate: (value) => value.trim().length > 0 || labels.reasonRequired,
            })}
          />
          {reasonError ? (
            <p id="order-review-rejection-reason-error" className="md-review-dialog__error" role="alert">
              {reasonError}
            </p>
          ) : null}
          <div className="md-review-dialog__actions">
            <Button type="button" variant="secondary" disabled={isPending} onClick={onClose}>
              {labels.cancel}
            </Button>
            <Button type="submit" variant="danger" loading={isPending} loadingLabel={labels.submitting}>
              <XCircleIcon className="md-review__button-icon" aria-hidden="true" />
              <span>{labels.submit}</span>
            </Button>
          </div>
        </form>
      </section>
    </div>
  );
}
